import type * as React from 'react';

import {
  DescriptionPage_SelfServeConfiguration_KeyInfoModule_ItemName as KeyInfoModuleItemName,
  DescriptionPage_SelfServeConfiguration_ModuleName as SelfServeConfigurationModuleNames,
} from '__generated__/graphql-types';

import type {
  SelfServeConfigProductOverviewModuleFragment,
  SelfServeConfigurationFragment,
} from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerSpecializationMarketingPageQuery';

export const ProductDetailItemNames = {
  ShareableCertificate: 'ShareableCertificate',
  Languages: 'Languages',
  IndustryCertification: 'IndustryCertification',
  RecentlyUpdated: 'RecentlyUpdated',
  ShareableCaseStudy: 'ShareableCaseStudy',
  Exclusive: 'Exclusive',
  SyllabusContent: 'SyllabusContent',
  LanguagesWithMLInfo: 'LanguagesWithMLInfo',
  InstallationRequirements: 'InstallationRequirements',
  Stackability: 'Stackability',
  ItemCounts: 'ItemCounts',
} as const;

export const UpsellCardNames = {
  CourseraPlusUpsellCard: 'CourseraPlusUpsellCard',
  DegreeUpsellCard: 'DegreeUpsellCard',
  EnterpriseUpsellCard: 'EnterpriseUpsellCard',
} as const;

const courseCountKey: keyof typeof KeyInfoModuleItemName = 'CourseCount';
export const KeyInformationItemNames = {
  ...KeyInfoModuleItemName,
  /** @deprecated Content count is now the intended naming convention. CourseCount is maintained for Contentful integration.  */
  [courseCountKey]: KeyInfoModuleItemName.CourseCount,
  /** Content Count is the replacement for Course Count and uses the existing 'COURSE_COUNT' value to support Contentful. */
  ContentCount: 'COURSE_COUNT',
  InstallationRequirements: 'InstallationRequirements',
  LearningMethod: 'LearningMethod',
  CourseSatisfactionScore: 'CourseSatisfactionScore',
  ProductType: 'ProductType',
  EarnCertificate: 'EarnCertificate',
} as const;

export type KeyInformationItemConfig = {
  name: (typeof KeyInformationItemNames)[keyof typeof KeyInformationItemNames];
  title?: string | null;
  subtitle?: string | null;
};

export type KeyInfoModule = {
  items?: KeyInformationItemConfig[];
};

export type ProductOverviewModuleConfig = Omit<
  SelfServeConfigProductOverviewModuleFragment,
  'heroModule' | 'keyInfoModule'
> & {
  heroModule?: SelfServeConfigProductOverviewModuleFragment['heroModule'] & { backgroundColor?: string };
  keyInfoModule?: SelfServeConfigProductOverviewModuleFragment['keyInfoModule'] & KeyInfoModule;
  removeLinksAboveFold?: boolean;
  showHeroLanguageInfo?: boolean;
  // Assign button only shows up for Admin users which takes them to the program’s assign flow
  showAssignButton?: boolean;
  hideCourseraPlus?: boolean;
  hideInstructors?: boolean;
  showGoogleBackgroundImage?: boolean;
  productDetailItems: Array<(typeof ProductDetailItemNames)[keyof typeof ProductDetailItemNames]>;
  backgroundImage?: string;
  backgroundImageElement?: React.ReactNode;
};

export const ModuleNames = {
  ...SelfServeConfigurationModuleNames,
  ProjectInstructions: 'ProjectInstructions',
  Reviews: 'Reviews',
  Recommendations: 'Recommendations',
  Benefits: 'Benefits',
  RegionalPromotionBanner: 'RegionalPromotionBanner',
  AiDisclaimer: 'AiDisclaimer',
  ComparisonTable: 'ComparisonTable',
  RelatedSkills: 'RelatedSkills',
  RecommendationCollection: 'RecommendationCollection',
} as const;

export type ModuleName = (typeof ModuleNames)[keyof typeof ModuleNames];

export type PageConfig = Omit<SelfServeConfigurationFragment, 'modules'> & {
  modules: Array<
    | SelfServeConfigurationFragment['modules'][number]
    | {
        name: (typeof ModuleNames)[keyof typeof ModuleNames];
      }
  >;
};
